import React, { useMemo } from 'react';
import classNames from 'classnames';

import { DynamicMediaItem } from '@uncinc/react-drupal-core';
import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';

import { Container, WysiwygField } from '@uncinc/uncinc-react-kitchen-sink';

import { ButtonWrapper } from '../../../components';
import useLayoutVariables from '../../../hooks/useLayoutVariables';
import type { BlockCTA } from '../../../types/uncinc_profile/blocks';

import './style.scss';

export interface CtaBlockProps {
  entity: ImmutableEntity<BlockCTA>;
}

const CTABlock: React.FC<CtaBlockProps> = ({ entity }) => {
  const {
    heroImageAspectRatio,
    backgroundImageRatio,
    imageColClasses,
    textColClasses,
    backgroundTextColClasses,
  } = useLayoutVariables('blockCTA');

  const ctaBlock = entity.toJS();
  const title = ctaBlock.info[0].value;
  const body = ctaBlock.body[0]?.processed;
  const image = ctaBlock.field_media[0];
  const imageAsBackground = ctaBlock.field_use_as_background[0]?.value === true;
  const linkTitle = ctaBlock.field_link[0].title;
  const linkUri = ctaBlock.field_link[0].alias || ctaBlock.field_link[0].uri;
  const secondaryLinkTitle = ctaBlock.field_link_secondary[0]?.title;
  const secondaryLinkUri = ctaBlock.field_link_secondary[0]?.alias || ctaBlock.field_link_secondary[0]?.uri;
  const classes = classNames('Block', 'CTABlock');

  const contentColClasses = useMemo(() => (
    (!imageAsBackground && image)
      ? textColClasses
      : backgroundTextColClasses
  ), [backgroundTextColClasses, image, imageAsBackground, textColClasses]);

  return (
    <div className={classes}>
      <Container>
        <div className="row">
          {(!imageAsBackground && image) && (
            <div className={imageColClasses}>
              <div className="CTABlock__image-wrapper">
                <DynamicMediaItem
                  className="CTABlock__image"
                  media={image}
                  imageAspectRatio={heroImageAspectRatio}
                />
              </div>
            </div>
          )}

          <div className={contentColClasses}>
            <div className="CTABlock__wrapper">
              {(imageAsBackground && image) && (
                <div className="CTABlock__overlay">
                  <DynamicMediaItem
                    media={image}
                    imageAspectRatio={backgroundImageRatio}
                    className="CTABlock__background-image"
                  />
                </div>
              )}

              <div className="CTABlock__content">
                <h2 className="CTABlock__title">
                  {title}
                </h2>

                {body && (
                  <WysiwygField
                    className="CTABlock__body"
                    property="schema:text"
                    html={body}
                  />
                )}
              </div>

              <div className="CTABlock__links">
                <ButtonWrapper href={linkUri}>
                  {linkTitle}
                </ButtonWrapper>

                {secondaryLinkUri && (
                  <ButtonWrapper type="secondary" href={secondaryLinkUri}>
                    {secondaryLinkTitle}
                  </ButtonWrapper>
                )}
              </div>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default CTABlock;
